import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { FiEdit3, FiSave, FiX, FiMessageSquare } from 'react-icons/fi';

export default function ReportNote() {
  const [isOpen, setIsOpen] = useState(false);
  const [note, setNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = () => {
    setIsSaving(true);
    // Simulate API call
    setTimeout(() => {
      setIsSaving(false);
      setIsOpen(false);
    }, 1000);
  };

  return (
    <div className="mt-12 pt-8 border-t border-slate-100 print:hidden">
      <div className="flex flex-col items-start">
        {!isOpen ? (
          <motion.button
            layoutId="note-container"
            onClick={() => setIsOpen(true)}
            className="group flex items-center gap-3 px-5 py-3 bg-slate-50 hover:bg-blue-50 border border-slate-200 hover:border-blue-200 rounded-2xl transition-all shadow-sm"
          >
            <div className="w-8 h-8 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-400 group-hover:text-blue-500 group-hover:border-blue-100 transition-colors shadow-sm">
              <SafeIcon icon={FiEdit3} />
            </div>
            <span className="text-[11px] font-black text-slate-400 group-hover:text-blue-600 uppercase tracking-[0.2em]">
              Add Report Note
            </span>
          </motion.button>
        ) : (
          <motion.div
            layoutId="note-container"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full bg-white border-2 border-blue-100 rounded-[32px] p-8 shadow-xl shadow-blue-900/5 relative overflow-hidden"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center shadow-lg shadow-blue-200">
                  <SafeIcon icon={FiMessageSquare} />
                </div>
                <div>
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Report Annotation</h3>
                  <p className="text-[10px] font-bold text-slate-400 uppercase">Visible to internal auditors & management</p>
                </div>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-slate-100 rounded-full text-slate-400 transition-colors"
              >
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>

            <textarea
              autoFocus
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Type your notes here (e.g., 'Variance due to seasonal shift in COGS')..."
              className="w-full min-h-[120px] bg-slate-50 border-2 border-slate-100 rounded-2xl p-4 text-sm font-medium text-slate-700 placeholder:text-slate-300 outline-none focus:border-blue-200 focus:bg-white transition-all resize-none"
            />

            <div className="mt-6 flex justify-end gap-3">
              <button 
                onClick={() => setIsOpen(false)}
                className="px-6 py-2.5 rounded-full text-[11px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-600 transition-colors"
              >
                Discard
              </button>
              <button 
                onClick={handleSave}
                disabled={!note.trim() || isSaving}
                className="flex items-center gap-2 px-8 py-2.5 bg-slate-900 text-white rounded-full text-[11px] font-black uppercase tracking-widest hover:bg-black transition-all shadow-xl active:scale-95 disabled:opacity-50"
              >
                {isSaving ? (
                  <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <SafeIcon icon={FiSave} />
                )}
                {isSaving ? 'Saving...' : 'Save Note'}
              </button>
            </div>
            
            {/* Subtle background decoration */}
            <div className="absolute -right-6 -bottom-6 w-24 h-24 bg-blue-50 rounded-full blur-2xl pointer-events-none" />
          </motion.div>
        )}
      </div>

      {/* Print View: Only show notes if they exist */}
      {note && !isOpen && (
        <div className="hidden print:block mt-8 p-6 bg-slate-50 border-l-4 border-slate-300">
          <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Report Notes</h4>
          <p className="text-sm text-slate-700 italic">"{note}"</p>
        </div>
      )}
    </div>
  );
}